import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { deriveKey, generateSalt } from "@/lib/crypto";
import type { User, Session } from "@supabase/supabase-js";

const MARKER_KEY = "hb_2fa_marker";
const MARKER_TTL = 30 * 60 * 1000; // 30 minutes

function save2FAMarker(userId: string, rememberMe: boolean) {
  const value = JSON.stringify({ userId, verifiedAt: Date.now() });
  const store = rememberMe ? localStorage : sessionStorage;
  store.setItem(MARKER_KEY, value);
}

function check2FAMarker(userId: string): boolean {
  for (const store of [localStorage, sessionStorage]) {
    try {
      const raw = store.getItem(MARKER_KEY);
      if (!raw) continue;
      const marker = JSON.parse(raw);
      if (marker.userId === userId && Date.now() - marker.verifiedAt < MARKER_TTL) {
        return true;
      }
    } catch { /* ignore */ }
  }
  return false;
}

function clear2FAMarker() {
  localStorage.removeItem(MARKER_KEY);
  sessionStorage.removeItem(MARKER_KEY);
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  cryptoKey: CryptoKey | null;
  needs2FA: boolean;
  is2FAVerified: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: string | null }>;
  verify2FA: (code: string) => Promise<{ error: string | null }>;
  resend2FA: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cryptoKey, setCryptoKey] = useState<CryptoKey | null>(null);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const pendingPassword = useRef<string | null>(null);
  const rememberMeRef = useRef(false);
  const signingInRef = useRef(false);

  const checkAdminRole = useCallback(async (userId: string) => {
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (error) {
      console.error("Role check failed:", error);
      return false;
    }
    return data === true;
  }, []);

  // 1. Synchronous-only auth state listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        // Don't interfere during signIn flow
        if (signingInRef.current) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (!newSession?.user) {
          setIsAdmin(false);
          setCryptoKey(null);
          setIs2FAVerified(false);
          setNeeds2FA(false);
          setIsLoading(false);
        }
      }
    );

    // Initial session fetch - just sets user/session, effect below handles the rest
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      if (!signingInRef.current) {
        setSession(existing);
        setUser(existing?.user ?? null);
        if (!existing?.user) {
          setIsLoading(false);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // 2. Separate effect for admin role + marker checks (with cancellation)
  useEffect(() => {
    // Skip if signing in (signIn manages its own state)
    if (signingInRef.current) return;

    if (!user) {
      // No user - already handled by listener, but ensure loading is false
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    checkAdminRole(user.id).then((admin) => {
      if (cancelled) return;
      const markerValid = check2FAMarker(user.id);
      if (admin && markerValid) {
        setIsAdmin(true);
        setIs2FAVerified(true);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id, checkAdminRole]);

  // 3. signIn — always fresh, no session reuse
  const signIn = useCallback(async (email: string, password: string, rememberMe = false) => {
    rememberMeRef.current = rememberMe;
    signingInRef.current = true;

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };

      if (!data.user || !data.session) return { error: "Login failed" };

      const admin = await checkAdminRole(data.user.id);
      if (!admin) {
        await supabase.auth.signOut();
        return { error: "Not authorized" };
      }

      // If remember-me marker is still valid, skip 2FA
      if (check2FAMarker(data.user.id)) {
        setUser(data.user);
        setSession(data.session);
        setIsAdmin(true);
        setIs2FAVerified(true);
        setIsLoading(false);
        save2FAMarker(data.user.id, rememberMe); // refresh TTL
        return { error: null };
      }

      // No valid marker - need 2FA
      setUser(data.user);
      setSession(data.session);
      pendingPassword.current = password;

      // Send 2FA code with 15-second timeout
      try {
        const result = await Promise.race([
          supabase.functions.invoke("send-2fa-code", {
            headers: { Authorization: `Bearer ${data.session.access_token}` },
          }),
          new Promise<{ error: Error }>((_, reject) =>
            setTimeout(() => reject(new Error("timeout")), 15000)
          ),
        ]);

        if (result.error) {
          console.error("Failed to send 2FA code:", result.error);
          return { error: "Failed to send verification code" };
        }
      } catch {
        return { error: "Request timed out. Please try again." };
      }

      setNeeds2FA(true);
      setIs2FAVerified(false);
      setIsAdmin(false);
      setIsLoading(false);

      return { error: null };
    } finally {
      signingInRef.current = false;
    }
  }, [checkAdminRole]);

  const verify2FA = useCallback(async (code: string) => {
    if (!session) return { error: "No session" };

    const { data, error } = await supabase.functions.invoke("verify-2fa-code", {
      body: { code },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) return { error: "Verification failed" };
    if (!data.verified) return { error: data.error || "Invalid code" };

    setIs2FAVerified(true);
    setIsAdmin(true);
    setNeeds2FA(false);

    if (user) {
      save2FAMarker(user.id, rememberMeRef.current);
    }

    if (user && pendingPassword.current) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("encryption_salt")
        .eq("id", user.id)
        .single();

      let salt = profile?.encryption_salt;
      if (!salt) {
        salt = generateSalt();
        await supabase
          .from("profiles")
          .update({ encryption_salt: salt })
          .eq("id", user.id);
      }

      const key = await deriveKey(pendingPassword.current, salt);
      setCryptoKey(key);
      pendingPassword.current = null;
    }

    return { error: null };
  }, [session, user]);

  const resend2FA = useCallback(async () => {
    if (!session) return { error: "No session" };
    const { error } = await supabase.functions.invoke("send-2fa-code", {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (error) return { error: "Failed to resend code" };
    return { error: null };
  }, [session]);

  // 4. signOut - backend first, then clear state
  const signOut = useCallback(async () => {
    clear2FAMarker();
    pendingPassword.current = null;
    await supabase.auth.signOut();
    // onAuthStateChange will fire with null and clear user/session,
    // but explicitly reset everything as well:
    setCryptoKey(null);
    setIsAdmin(false);
    setUser(null);
    setSession(null);
    setNeeds2FA(false);
    setIs2FAVerified(false);
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isLoading, cryptoKey, needs2FA, is2FAVerified, signIn, verify2FA, resend2FA, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
